import { useState, useEffect, useCallback } from "react";

import { useBusinessContext } from "0.Mine/2.Context/businessContext";
import { Client, CustomField } from "0.Mine/3.Interfaces/Client";

import { useHandleClients } from "../CRM/client";

const extractCustomFieldsFromResponse = (customers: any[]): CustomField[] => {
  const fieldsMap = new Map<string, CustomField>();

  customers.forEach((customer) => {
    if (customer.preferences && typeof customer.preferences === "object") {
      Object.keys(customer.preferences).forEach((key) => {
        if (!fieldsMap.has(key)) {
          const value = customer.preferences[key];
          let type: "text" | "select" | "number" = "text";

          if (typeof value === "number") {
            type = "number";
          }

          fieldsMap.set(key, {
            id: key,
            name: key.charAt(0).toUpperCase() + key.slice(1),
            type: type,
          });
        }
      });
    }
  });

  return Array.from(fieldsMap.values());
};

const initialCustomFields: CustomField[] = [];

export function useCustomFields() {
  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { session } = useBusinessContext();
  const { getClients, bulkUpdateCustomerColumns } = useHandleClients();

  const loadCustomFields = useCallback(async () => {
    try {
      setIsLoading(true);

      if (!session || !session.selectedBusiness) {
        setCustomFields(initialCustomFields);
        return;
      }

      const response = await getClients(session.selectedBusiness.id);

      if (response && response.customers && response.customers.length > 0) {
        const extractedFields = extractCustomFieldsFromResponse(response.customers);

        if (extractedFields.length > 0) {
          setCustomFields(extractedFields);
          return;
        }
      }

      setCustomFields(initialCustomFields);
    } catch (err) {
      console.error("Error loading custom fields:", err);
      setError(err instanceof Error ? err : new Error("Failed to load custom fields"));
      setCustomFields(initialCustomFields);
    } finally {
      setIsLoading(false);
    }
  }, [session?.selectedBusiness?.id, getClients]);

  useEffect(() => {
    loadCustomFields();
  }, [session?.selectedBusiness?.id]);

  const addCustomField = async (field: Omit<CustomField, "id">) => {
    try {
      const fieldId = field.name.toLowerCase().replace(/[^a-z0-9]+/g, "_");

      const newField = {
        ...field,
        id: fieldId,
      };

      const updatedFields = [...customFields, newField];

      const preferences: Record<string, string> = {};
      const fieldTypes: Record<string, { type: string; options?: string[] }> = {};

      updatedFields.forEach((field) => {
        preferences[field.id] = "";

        fieldTypes[field.id] = {
          type: field.type,
          ...(field.options ? { options: field.options } : {}),
        };
      });

      if (session?.selectedBusiness?.id) {
        const response = await bulkUpdateCustomerColumns(
          session.selectedBusiness.id,
          preferences,
          fieldTypes
        );

        if (!response || !response.success) {
          throw new Error(response?.message || "API call failed");
        }

        setCustomFields(updatedFields);
        return newField;
      } else {
        throw new Error("No business selected");
      }
    } catch (err) {
      console.error("Error adding custom field:", err);
      setError(err instanceof Error ? err : new Error("Failed to add custom field"));
      throw err;
    }
  };

  const updateCustomField = async (id: string, updates: Partial<CustomField>) => {
    try {
      const updatedFields = customFields.map((field) =>
        field.id === id ? { ...field, ...updates } : field
      );

      const preferences: Record<string, string> = {};
      const fieldTypes: Record<string, { type: string; options?: string[] }> = {};

      updatedFields.forEach((field) => {
        preferences[field.id] = "";
        fieldTypes[field.id] = {
          type: field.type,
          ...(field.options ? { options: field.options } : {}),
        };
      });

      if (session?.selectedBusiness?.id) {
        const response = await bulkUpdateCustomerColumns(
          session.selectedBusiness.id,
          preferences,
          fieldTypes
        );

        if (!response || !response.success) {
          throw new Error(response?.message || "API call failed");
        }

        setCustomFields(updatedFields);
        return updatedFields.find((field) => field.id === id);
      } else {
        throw new Error("No business selected");
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Failed to update custom field"));
      throw err;
    }
  };

  const deleteCustomField = async (id: string) => {
    try {
      if (!session?.selectedBusiness?.id) {
        throw new Error("No business selected");
      }

      const updatedFields = customFields.filter((field) => field.id !== id);

      const preferences: Record<string, string> = {};
      const fieldTypes: Record<string, { type: string; options?: string[] }> = {};

      updatedFields.forEach((field) => {
        preferences[field.id] = "";
        fieldTypes[field.id] = {
          type: field.type,
          ...(field.options ? { options: field.options } : {}),
        };
      });

      const response = await bulkUpdateCustomerColumns(
        session.selectedBusiness.id,
        preferences,
        fieldTypes
      );

      if (!response || !response.success) {
        throw new Error(response?.message || "API call failed");
      }

      setCustomFields(updatedFields);
    } catch (err) {
      console.error("Error deleting custom field:", err);
      setError(err instanceof Error ? err : new Error("Failed to delete custom field"));
      throw err;
    }
  };

  return {
    customFields,
    isLoading,
    error,
    addCustomField,
    updateCustomField,
    deleteCustomField,
    refetch: loadCustomFields,
  } as const;
}

export function useClients() {
  const { session } = useBusinessContext();
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState({
    total: 0,
    currentPage: 1,
    pageSize: 25,
    lastPage: 1,
  });
  const { getClients, addSingleClient } = useHandleClients();

  const loadClients = useCallback(
    async (
      page: number = 1,
      pageSize: number = 25,
      search?: string,
      filters?: {
        name?: string;
        email?: string;
        phone?: string;
        source?: string;
        customer_group?: string;
        total_spent_min?: number;
        total_spent_max?: number;
        created_from?: string;
        created_to?: string;
        updated_from?: string;
        updated_to?: string;
        last_login_from?: string;
        last_login_to?: string;
        tag?: string;
      }
    ) => {
      try {
        if (!session?.selectedBusiness?.id) {
          setClients([]);
          setIsLoading(false);
          return;
        }

        setIsLoading(true);

        const response = await getClients(session.selectedBusiness.id, {
          page,
          limit: pageSize,
          name: search,
          orderby: "updated",
          order: "DESC",
          ...filters,
        });

        if (response && response.customers) {
          const mappedClients = response.customers.map((customer: any) => ({
            id: customer.id.toString(),
            name: `${customer.name} ${customer.surname || ""}`.trim(),
            email: customer.email || "",
            phone: customer.phone || "",
            company: customer.customer_group || "",
            status: determineClientStatus(customer),
            lastContact: customer.last_login || customer.updated || customer.created,
            value: customer.total_spent ? parseFloat(customer.total_spent) : 0,
            tags: customer.tags || [],
            customFields: customer.preferences || {},
            mailing: Boolean(customer.mailing),
            phonecall: Boolean(customer.phonecall),
            whatsapp: Boolean(customer.whatsapp),
            notes: customer.notes || "",
            source: customer.source || "",
            created: customer.created,
            updated: customer.updated,
          }));

          setClients(mappedClients);

          if (response.pagination) {
            setPagination({
              total: response.pagination.total || 0,
              currentPage: page,
              pageSize: pageSize,
              lastPage:
                response.pagination.lastPage ||
                Math.ceil((response.pagination.total || 0) / pageSize),
            });
          } else {
            const total = response.total || mappedClients.length;
            setPagination({
              total,
              currentPage: page,
              pageSize,
              lastPage: Math.ceil(total / pageSize),
            });
          }
        } else {
          setClients([]);
          setPagination({
            total: 0,
            currentPage: 1,
            pageSize: 25,
            lastPage: 1,
          });
        }
      } catch (err) {
        console.error("Error loading clients:", err);
        setError(err instanceof Error ? err : new Error("Failed to load clients"));
        setClients([]);
      } finally {
        setIsLoading(false);
      }
    },
    [session?.selectedBusiness?.id, getClients]
  );

  const determineClientStatus = (customer: any): "active" | "inactive" => {
    if (customer.total_spent && parseFloat(customer.total_spent) > 0) {
      return "active";
    }

    if (customer.last_login) {
      const lastLoginDate = new Date(customer.last_login);
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      if (lastLoginDate >= thirtyDaysAgo) {
        return "active";
      } else {
        return "inactive";
      }
    }

    return "inactive";
  };

  return {
    clients,
    isLoading,
    error,
    pagination,
    loadClients,
    addClient: addSingleClient,
    updateClient: async (id: string, updates: Partial<Client>) => {
      try {
        await new Promise((resolve) => setTimeout(resolve, 500));
        await loadClients(pagination.currentPage, pagination.pageSize);
        return { id, ...updates };
      } catch (err) {
        setError(err instanceof Error ? err : new Error("Failed to update client"));
        throw err;
      }
    },
    deleteClient: async (id: string) => {
      try {
        await new Promise((resolve) => setTimeout(resolve, 500));
        await loadClients(pagination.currentPage, pagination.pageSize);
      } catch (err) {
        setError(err instanceof Error ? err : new Error("Failed to delete client"));
        throw err;
      }
    },
  };
}
